import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../app/store";
import { ApiEndpoints } from "../api/endpoints";
import { Box, BoxVideo, LevelBox } from "../modal/box";
import { Item } from "../modal/Item";
import { ComputeItem } from "../modal/Compute_Item";
import { Row } from "../modal/row";
import { api, constructBoxComputeType, getStockDetails, getStockThresholdForItems, handleLogoutUser, isLowStockBox } from "../utils/utils";
import { useNavigate } from "react-router-dom";


interface BoxState {
  isLoading: boolean;
  isNeedToRefresh: boolean;
  boxes: Box[];
  currentPage: number;
  boxesPerPage: number;
  totalItems: number;
  searchString: string;
  createBoxStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  updateBoxStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  selectedBox: Box | null;
  isLoadingBoxDetails: boolean;
  getBoxDetailsByIdStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  getBoxDetailsByIdError: string | null;
  isCreatingOrUpdating: boolean;
  duplicateBoxStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  duplicateBoxError: string | null;
  isDuplicateBoxInprogress: boolean;
  duplicateBoxId: string | null;  // Track boxId being duplicated
  deleteBoxStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  deleteBoxError: string | null;
  isDeleteBoxInprogress: boolean;
  deleteBoxId: string | null;  // Track boxId being deleted
  levelBoxes: LevelBox[];
  getVipBoxesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  getVipBoxesError: string | null;
  selectedLevelBox: LevelBox | null;
  allBoxes: Box[];
  getAllBoxesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  getAllBoxesError: string | null;
  updateLevelBoxStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  updateLevelBoxError: string | null;
  boxVideos: BoxVideo[] | null;
  boxVideosStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  boxVideosError: string | null;
  fetchRetiredBoxesCountStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  fetchRetiredBoxesCountError: string | null;
  retiredBoxesCount: number;
  retiredBoxesFilter: boolean; // New state for low stock filter
  items: Item[];
}



const initialState: BoxState = {
  isLoading: false,
  isNeedToRefresh: false,
  boxes: [],
  currentPage: 1,
  boxesPerPage: 20, // Set your desired items per page
  totalItems: 0,
  searchString: "",
  createBoxStatus: 'idle',
  updateBoxStatus: 'idle',
  error: null,
  selectedBox: null,
  getBoxDetailsByIdStatus: 'idle',
  getBoxDetailsByIdError: null,
  isLoadingBoxDetails: false,
  isCreatingOrUpdating: false,
  duplicateBoxStatus: 'idle',
  duplicateBoxError: null,
  isDuplicateBoxInprogress: false,
  duplicateBoxId: null,
  deleteBoxStatus: 'idle',
  deleteBoxError: null,
  isDeleteBoxInprogress: false,
  deleteBoxId: null,
  levelBoxes: [],
  getVipBoxesStatus: 'idle',
  getVipBoxesError: null,
  selectedLevelBox: null,
  allBoxes: [],
  getAllBoxesStatus: 'idle',
  getAllBoxesError: null,
  updateLevelBoxStatus: 'idle',
  updateLevelBoxError: null,
  boxVideos: null,
  boxVideosStatus: 'idle',
  boxVideosError: null,
  fetchRetiredBoxesCountStatus: 'idle',
  fetchRetiredBoxesCountError: null,
  retiredBoxesCount: 0,
  retiredBoxesFilter: false,
  items: [],
};


// Add the low_stock async thunk
export const fetchRetiredBoxesCount = createAsyncThunk<
  { count: number; }, // Expected response type
  void, // No argument needed
  { dispatch: AppDispatch, rejectValue: string } // Reject type
>(
  "items/fetchRetiredBoxes",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get(`${ApiEndpoints.RETIRED_BOXES}`);
      return response.data;
    } catch (error: any) {

      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      // Return the error message
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch low stock data"
      );
    }
  }
);


export const duplicateBox = createAsyncThunk<
  void,
  { id: string; name: string }, // The box_id and name will be passed as an object
  { dispatch: AppDispatch, rejectValue: string }
>(
  "/boxes/duplicateBox",
  async ({ id, name }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${ApiEndpoints.BOXES}/duplicate?box_id=${id}&name=${name}`);
      dispatch(getBoxesContent()); // Now it knows how to handle getBoxesContent
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      // Return the error message
      return rejectWithValue(error.response.data.detail || "Failed to duplicate box");
    }
  }
);


export const updateLevelBox = createAsyncThunk<
  void,
  { boxId: string; vipLevel: string }, // The box_id and name will be passed as an object
  { dispatch: AppDispatch, rejectValue: string }
>(
  "/boxes/updateLevelBox",
  async ({ boxId, vipLevel }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${ApiEndpoints.BOXES}/mark_vip?box_id=${boxId}&vip_level=${vipLevel}`);
      dispatch(getVipBoxes()); // Now it knows how to handle getBoxesContent
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail || "Failed to mark vip box");
      }
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);

export const deleteBox = createAsyncThunk<
  void,
  { id: string }, // The box_id and name will be passed as an object
  { dispatch: AppDispatch, rejectValue: string }
>(
  "/boxes/deleteBox",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get(`${ApiEndpoints.BOXES}/delete/${id}`);
      dispatch(getBoxesContent()); // Now it knows how to handle getBoxesContent
      return response.data;
    } catch (error: any) {

      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail || "Failed to delete box");
      }
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);


export const get1000Items = async () => {
  try {
    // Define parameters
    const offset = 0;
    const limit = 1000;

    // Construct the URL with parameters
    const apiWithParams = `${ApiEndpoints.ITEMS}?offset=${offset}&limit=${limit}&retired=${false}`;

    const response = await api.get(apiWithParams);
    const itemsArray = Item.parseItems(response.data.list);
    return itemsArray;
  } catch (error) {
    console.error("Error fetching items:", error);
    throw error;
  }
};



export async function boxCompute(rows: Row[]): Promise<ComputeItem | null> {
  try {
    let ep = `${ApiEndpoints.COMPUTE_BOX}`;
    const computeItems = constructBoxComputeType(rows);
    const response = await api.post(ep, computeItems);
    if (response.status == 200) {
      let cmputeItem: ComputeItem = response.data;
      console.log("Box Compute ComputeItem => " + cmputeItem.other_items.length);
      return cmputeItem;
    }
    return null;

  } catch (error) {
    console.error("Error boxCompute() : ", error);
    throw error;
  }
};

export const fetch1000Items = createAsyncThunk<
  { list: Item[] }, // Return type
  void, // Argument type
  { dispatch: AppDispatch; rejectValue: string } // ThunkAPI type
>(
  "app/fetch1000Items",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const offset = 0;
      const limit = 1000;
      const response = await api.get(
        `${ApiEndpoints.ITEMS}?offset=${offset}&limit=${limit}&retired=${false}`
      );
      const itemsArray = Item.parseItems(response.data.list);
      return { list: itemsArray };
    } catch (error: any) {
      console.error("Error fetching items:", error);

      // Check if the error is a 401
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      // Return the error message
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred"
      );
    }
  }
);

export const getBoxesContent = createAsyncThunk<
  { list: Box[]; total: number },
  void,
  { dispatch: AppDispatch, state: RootState }
>(
  "/boxes/content",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const boxesState = getState().boxes;

      if (!boxesState) {
        console.error("Boxes state is not defined");
        return rejectWithValue("Boxes state is not defined");
      }

      // Fetch items and wait for completion
      const fetchItemsResult = await dispatch(fetch1000Items());

      // Check if fetch1000Items was successful
      if (fetch1000Items.fulfilled.match(fetchItemsResult)) {
        // Get updated state after items are fetched
        const updatedState = getState();
        const { settings } = updatedState.settings;
        const lowStockThreshold = getStockThresholdForItems(settings);

        const { currentPage, boxesPerPage, searchString } = updatedState.boxes;
        const offset = (currentPage - 1) * boxesPerPage;

        const queryParams: any = {
          offset,
          limit: boxesPerPage,
          retired: boxesState.retiredBoxesFilter,
        };

        if (searchString.trim().length > 0) {
          queryParams.q = searchString.trim();
        }

        const response = await api.get<{ list: Box[]; total: number }>(
          ApiEndpoints.BOXES,
          {
            params: queryParams,
          }
        );

        // Use the updated items for stock calculations
        const boxesWithStockInfo = response.data.list.map((box) => {
          const stockDetails = getStockDetails(
            updatedState.boxes.items, // Use updated items here
            box,
            lowStockThreshold
          );
          return {
            ...box,
            lowStockItemsCount: stockDetails.lowStockItemsCount,
            outOfStockItemsCount: stockDetails.outOfStockItemsCount,
            totalItems: stockDetails.totalItems,
          };
        });

        return { list: boxesWithStockInfo, total: response.data.total };
      } else {
        console.error("Failed to fetch items");
        return rejectWithValue("Failed to fetch items");
      }
    } catch (error: any) {
      console.log(error);
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);


export const getAllBoxes = createAsyncThunk<
  { list: Box[] },
  void,
  { dispatch: AppDispatch, rejectValue: string }
>(
  "/boxes/allboxes",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const queryParams: Record<string, string | number> = {
        offset: 0,
        limit: 1000,
      };

      const response = await api.get<{ list: Box[]; }>(ApiEndpoints.BOXES, {
        params: queryParams,
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      // Check if the error is a 401
      if (error.response && error.response.status === 401) {
        // Dispatch the logout action
        handleLogoutUser(dispatch, useNavigate());
      }
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);



// Create the getVipBoxes async thunk
export const getVipBoxes = createAsyncThunk<
  LevelBox[],
  void,
  { dispatch: AppDispatch; rejectValue: string }
>(
  '/boxes/vipBoxes',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get<Box[]>(`${ApiEndpoints.VIP_BOXES}`, {
        headers: {
          accept: 'application/json',
        },
      });

      // Only VIP boxes where `is_vip` is true
      const vipBoxes = response.data.filter((box) => box.is_vip);

      // List of names for each level from 0 to 10
      const levelNames = [
        'Basic', 'Essence', 'Asent', 'Glow', 'Sparkle',
        'Gleam', 'Aura', 'Elegance', 'Prestige', 'Sovereign',
        'Supreme Luxe'
      ];

      // Map each level from 0 to 10 to a LevelBox object
      const levels: LevelBox[] = Array.from({ length: 11 }, (_, level) => {
        const boxForLevel = vipBoxes.find((box) => box.vip_level === level);
        return { level, name: levelNames[level], box: boxForLevel || null };
      });

      return levels;
    } catch (error: any) {
      console.error(error);

      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      return rejectWithValue(error.message || 'Failed to fetch VIP boxes');
    }
  }
);


// Define the createBox thunk with appropriate types
export const createBox = createAsyncThunk<
  Box,
  Box,
  { dispatch: AppDispatch, rejectValue: string }
>(
  "/boxes/createBox",
  async (boxData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post<Box>(ApiEndpoints.BOXES, boxData);

      // Dispatch getBoxesContent to refresh the list of boxes
      dispatch(getBoxesContent());

      return response.data;
    } catch (error: any) {
      // Capture the error response
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);

// Define the thunk with appropriate types
export const updateBox = createAsyncThunk<
  Box,
  Box,
  { dispatch: AppDispatch, rejectValue: string }
>(
  "/boxes/updateBox",
  async ({ id, ...boxData }, { dispatch, rejectWithValue }) => {
    try {
      const urlString = `${ApiEndpoints.BOXES}/${id}`;
      const response = await api.post<Box>(urlString, boxData);

      // Correctly typed dispatch
      dispatch(getBoxesContent()); // Now it knows how to handle getBoxesContent

      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);

// Define the getBoxDetailsById thunk
export const getBoxDetailsById = createAsyncThunk<
  Box,
  string,
  { dispatch: AppDispatch, rejectValue: string }
>(
  "/boxes/getBoxDetailsById",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get<Box>(`${ApiEndpoints.BOXES}/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      }

      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.detail || "Failed to fetch box details");
      }
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);


// Thunk to fetch box details from the API
export const fetchBoxVideos = createAsyncThunk<
  any, // The return type (can be more specific based on the response structure)
  string, // Argument type (boxId)
  {
    dispatch: AppDispatch; // Dispatch type
    state: RootState; // State type
    rejectValue: string; // Optional reject value type (string in this case)
  }
>(
  "boxDetails/fetchBoxVideos",
  async (boxId, { rejectWithValue, getState, dispatch }) => {
    try {

      // Fetch the box details using axios (or your API module)
      const response = await api.get(`/get-box-videos?box_id=${boxId}`);

      // Return the fetched data
      return response.data;
    } catch (error: any) {
      // Check for 401 Unauthorized status
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate()); // Call logout action
        return rejectWithValue("Unauthorized access - logging out");
      }

      // Handle error and use rejectWithValue for rejection
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue("Failed to fetch box details");
      }
    }
  }
);




export const boxesSlice = createSlice({
  name: "boxes",
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
      state.isNeedToRefresh = true; // Trigger refresh when the page changes
    },
    setSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload;
    },
    setSelectedBox: (state, action: PayloadAction<Box | null>) => {
      state.selectedBox = action.payload;
    },
    setSelectedLevelBox: (state, action: PayloadAction<LevelBox | null>) => {
      state.selectedLevelBox = action.payload;
    },
    resetCreateBoxStatus: (state) => {
      state.createBoxStatus = 'idle';
      state.error = null;
    },
    resetUpdateBoxStatus: (state) => {
      state.updateBoxStatus = 'idle';
      state.error = null;
    },
    resetDuplicateBoxStatus: (state) => {
      state.duplicateBoxStatus = 'idle';
      state.duplicateBoxError = null;
      state.duplicateBoxId = null; // Reset the boxId being tracked
    },
    resetDeleteBoxStatus: (state) => {
      state.deleteBoxStatus = 'idle';
      state.deleteBoxError = null;
      state.deleteBoxId = null; // Reset the boxId being tracked
    },
    resetGetAllBoxesStatus: (state) => {
      state.getAllBoxesStatus = 'idle';
      state.getAllBoxesError = null;
    },
    resetUpdateLevelBoxStatus: (state) => {
      state.updateLevelBoxStatus = 'idle';
      state.updateLevelBoxError = null;
    },
    setRetiredBoxesFilter: (state, action: PayloadAction<boolean>) => {
      state.retiredBoxesFilter = action.payload; // Update the filter state
      state.isNeedToRefresh = true; // Trigger refresh
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBoxesContent.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBoxesContent.fulfilled, (state, action) => {
        state.boxes = action.payload.list;
        state.totalItems = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getBoxesContent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch boxes content";
      })
      .addCase(createBox.pending, (state) => {
        state.isLoading = true;
        state.isNeedToRefresh = false;
        state.createBoxStatus = 'loading';
        state.error = null;
        state.isCreatingOrUpdating = true;
      })
      .addCase(createBox.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isNeedToRefresh = true;
        state.createBoxStatus = 'succeeded';
        state.selectedBox = action.payload;
        state.isCreatingOrUpdating = false;
      })
      .addCase(createBox.rejected, (state, action) => {
        state.isLoading = false;
        state.isNeedToRefresh = false;
        state.createBoxStatus = 'failed';
        state.error = action.payload || action.error.message || "Failed to create box";
        state.isCreatingOrUpdating = false;
      })
      .addCase(updateBox.pending, (state) => {
        state.isLoading = true;
        state.isNeedToRefresh = false;
        state.updateBoxStatus = 'loading';
        state.error = null;
        state.isCreatingOrUpdating = true;
      })
      .addCase(updateBox.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isNeedToRefresh = true;
        state.updateBoxStatus = 'succeeded';
        state.selectedBox = action.payload;
        state.isCreatingOrUpdating = false;
      })
      .addCase(updateBox.rejected, (state, action) => {
        state.isLoading = false;
        state.isNeedToRefresh = false;
        state.updateBoxStatus = 'failed';
        state.error = action.payload || action.error.message || "Failed to update box";
        state.isCreatingOrUpdating = false;
      })
      .addCase(getBoxDetailsById.pending, (state) => {
        state.isLoadingBoxDetails = true;
        state.getBoxDetailsByIdStatus = 'loading';
        state.getBoxDetailsByIdError = null;
      })
      .addCase(getBoxDetailsById.fulfilled, (state, action) => {
        state.isLoadingBoxDetails = false;
        state.getBoxDetailsByIdStatus = 'succeeded';
        state.selectedBox = action.payload;
      })
      .addCase(getBoxDetailsById.rejected, (state, action) => {
        state.isLoadingBoxDetails = false;
        state.getBoxDetailsByIdStatus = 'failed';
        state.getBoxDetailsByIdError = action.payload || action.error.message || "Failed to fetch box details";
      })
      .addCase(duplicateBox.pending, (state, action) => {
        state.duplicateBoxStatus = 'loading';
        state.duplicateBoxError = null;
        state.isDuplicateBoxInprogress = true;
        state.duplicateBoxId = action.meta.arg.id;
      })
      .addCase(duplicateBox.fulfilled, (state) => {
        state.duplicateBoxStatus = 'succeeded';
        state.isDuplicateBoxInprogress = false;
        state.isNeedToRefresh = true; // Refresh the boxes list if needed
        state.duplicateBoxId = null; // Clear boxId after failure
      })
      .addCase(duplicateBox.rejected, (state, action) => {
        state.duplicateBoxStatus = 'failed';
        state.duplicateBoxError = action.payload || action.error.message || "Failed to duplicate box";
        state.isDuplicateBoxInprogress = false;
        state.duplicateBoxId = null; // Clear boxId after failure
      })
      .addCase(deleteBox.pending, (state, action) => {
        state.deleteBoxStatus = 'loading';
        state.deleteBoxError = null;
        state.isDeleteBoxInprogress = true;
        state.deleteBoxId = action.meta.arg.id;
      })
      .addCase(deleteBox.fulfilled, (state) => {
        state.deleteBoxStatus = 'succeeded';
        state.isDeleteBoxInprogress = false;
        state.isNeedToRefresh = true; // Refresh the boxes list if needed
        state.deleteBoxId = null; // Clear boxId after failure
      })
      .addCase(deleteBox.rejected, (state, action) => {
        state.deleteBoxStatus = 'failed';
        state.deleteBoxError = action.payload || action.error.message || "Failed to duplicate box";
        state.isDeleteBoxInprogress = false;
        state.deleteBoxId = null; // Clear boxId after failure
      })
      .addCase(getVipBoxes.pending, (state) => {
        state.getVipBoxesStatus = 'loading';
        state.getVipBoxesError = null;
      })
      .addCase(getVipBoxes.fulfilled, (state, action) => {
        state.getVipBoxesStatus = 'succeeded';
        state.levelBoxes = action.payload; // Update to use levelBoxes
      })
      .addCase(getVipBoxes.rejected, (state, action) => {
        state.getVipBoxesStatus = 'failed';
        state.getVipBoxesError = action.payload || action.error.message || 'Failed to fetch box details';
      })

      .addCase(getAllBoxes.pending, (state) => {
        state.getAllBoxesStatus = 'loading';
        state.getAllBoxesError = null;
      })
      .addCase(getAllBoxes.fulfilled, (state, action) => {
        state.getAllBoxesStatus = 'succeeded';
        // Sort the boxes by `finalValue` in ascending order
        state.allBoxes = action.payload.list.sort((a, b) => {
          const finalValueA = a.final_value ?? 0; // Use 0 if final_value is undefined
          const finalValueB = b.final_value ?? 0; // Use 0 if final_value is undefined
          return finalValueA - finalValueB; // Sort ascending
        });
      })
      .addCase(getAllBoxes.rejected, (state, action) => {
        state.getAllBoxesStatus = 'failed';
        state.getAllBoxesError = action.payload || action.error.message || 'Failed to fetch all boxes';
      })
      .addCase(updateLevelBox.pending, (state) => {
        state.updateLevelBoxStatus = 'loading';
        state.updateLevelBoxError = null;
      })
      .addCase(updateLevelBox.fulfilled, (state, action) => {
        state.updateLevelBoxStatus = 'succeeded';
      })
      .addCase(updateLevelBox.rejected, (state, action) => {
        state.updateLevelBoxStatus = 'failed';
        state.updateLevelBoxError = action.payload || action.error.message || 'Failed to update level box';
      })
      .addCase(fetchBoxVideos.pending, (state) => {
        state.boxVideosStatus = 'loading';
        state.boxVideosError = null;
      })
      .addCase(
        fetchBoxVideos.fulfilled,
        (state, action: PayloadAction<BoxVideo[]>) => {
          state.boxVideos = action.payload;
          state.boxVideosStatus = 'succeeded';
        }
      )
      .addCase(fetchBoxVideos.rejected, (state, action) => {
        // Use a type assertion to handle the type
        const castAction = action as PayloadAction<string | undefined>;
        state.boxVideosStatus = 'failed';
        state.boxVideosError =
          castAction.payload || "An error occurred while fetching box details.";
      })
      .addCase(fetchRetiredBoxesCount.pending, (state) => {
        state.fetchRetiredBoxesCountStatus = 'loading';
        state.fetchRetiredBoxesCountError = null;
      })
      .addCase(
        fetchRetiredBoxesCount.fulfilled,
        (state, action) => {
          state.retiredBoxesCount = action.payload.count;
          state.fetchRetiredBoxesCountStatus = 'succeeded';
        }
      )
      .addCase(fetchRetiredBoxesCount.rejected, (state, action) => {
        // Use a type assertion to handle the type
        const castAction = action as PayloadAction<string | undefined>;
        state.fetchRetiredBoxesCountStatus = 'failed';
        state.fetchRetiredBoxesCountError =
          castAction.payload || "An error occurred while fetching box details.";
      })
      .addCase(fetch1000Items.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetch1000Items.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.list;;
        state.error = null;
      })
      .addCase(fetch1000Items.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentPage,
  setSearchString,
  setSelectedBox,
  setSelectedLevelBox,
  resetGetAllBoxesStatus,
  resetCreateBoxStatus,
  resetUpdateBoxStatus,
  resetDuplicateBoxStatus,
  resetDeleteBoxStatus,
  resetUpdateLevelBoxStatus,
  setRetiredBoxesFilter
} = boxesSlice.actions;

export default boxesSlice.reducer;

